import { Suspense } from 'react';
import { useTracking } from 'react-tracking';
import loadable from '@loadable/component';
import useResizeObserver from 'use-resize-observer';

import { Heading } from '@simplywallst/ui-core/next';

import useUser from '@/hooks/useUser';

import type { CountryISO } from '@/constants/countries';
import { isCountryISO } from '@/constants/countries';

import { usePopularBrokerage } from './usePopularBrokerage';
import type { Brokerage } from '../../hooks/useBrokerages';
import { BrokerageItem, BrokerageItemSkeleton } from './BrokerageItem';
import { DraggableContainerLoading } from './DraggableContainer';

const DraggableContainer = loadable(() => import('./DraggableContainer'), {
  resolveComponent: (c) => c.DraggableContainer,
});

interface Props {
  countryISO: CountryISO;
  onClick: (data: Brokerage) => void;
}

const DraggableBrokerages = ({ countryISO, onClick }: Props) => {
  const { brokerages, heading } = usePopularBrokerage(countryISO);

  if (brokerages.length === 0) return;

  return (
    <>
      <Heading as="h3" size="small" weight="normal" className="px-x2 sm:px-x3">
        {heading}
      </Heading>
      <DraggableContainer>
        {({ isClickAllowed }) =>
          brokerages.map((b) => (
            <BrokerageItem
              key={b.id}
              onClick={() => isClickAllowed?.() && onClick(b)}
              {...b}
            />
          ))
        }
      </DraggableContainer>
    </>
  );
};

const DraggableBrokeragesSkeleton = () => (
  <>
    <Heading as="h3" size="small" weight="normal" className="px-x2 sm:px-x3">
      Top brokers
    </Heading>
    <DraggableContainerLoading>
      <BrokerageItemSkeleton />
      <BrokerageItemSkeleton />
      <BrokerageItemSkeleton />
    </DraggableContainerLoading>
  </>
);

const NonDraggableBrokerages = ({ countryISO, onClick }: Props) => {
  const { brokerages, heading } = usePopularBrokerage(countryISO);

  if (brokerages.length === 0) return;

  return (
    <>
      <Heading as="h3" size="small" weight="normal" className="px-x2 sm:px-x3">
        {heading}
      </Heading>
      <div className="grid gap-x1 px-x2 @[350px]/top:grid-cols-2 sm:px-x3">
        {brokerages.map((b) => (
          <BrokerageItem key={b.id} onClick={() => onClick(b)} {...b} />
        ))}
      </div>
    </>
  );
};

const NonDraggableBrokeragesSkeleton = () => (
  <>
    <Heading as="h3" size="small" weight="normal" className="px-x2 sm:px-x3">
      Top brokers
    </Heading>
    <div className="grid gap-x1 px-x2 @[350px]/top:grid-cols-2 sm:px-x3">
      <BrokerageItemSkeleton />
      <BrokerageItemSkeleton />
      <BrokerageItemSkeleton />
      <BrokerageItemSkeleton />
    </div>
  </>
);

export const PopularBrokerage = ({
  onSelect,
}: {
  onSelect: (data: Brokerage) => void;
}) => {
  const { Track, trackEvent } = useTracking({
    type: 'track',
    modifier: 'connectWithBrokerage',
  });

  const { data: user } = useUser();

  let countryISO: CountryISO = 'us';

  const defaultMarketISO = user.defaultMarketISO;
  const userCountryISO = user.countryISO?.toLowerCase();

  if (isCountryISO(defaultMarketISO)) {
    countryISO = defaultMarketISO;
  } else if (isCountryISO(userCountryISO)) {
    countryISO = userCountryISO;
  }

  const { ref, width } = useResizeObserver({
    box: 'border-box', // border-box for consistent sizing
  });
  const isDraggable = width ? width <= 430 : false;

  const handleOnClick = (b: Brokerage) => {
    trackEvent({ action: 'select', brokerageId: b.id, isPopular: true });
    onSelect(b);
  };

  return (
    <Track>
      <section
        ref={ref}
        className="grid gap-x1_5 @container/top sm:gap-x2"
        data-cy-id="portfolio-top-brokers"
      >
        {isDraggable ? (
          <Suspense fallback={<DraggableBrokeragesSkeleton />}>
            <DraggableBrokerages
              countryISO={countryISO}
              onClick={handleOnClick}
            />
          </Suspense>
        ) : (
          <Suspense fallback={<NonDraggableBrokeragesSkeleton />}>
            <NonDraggableBrokerages
              countryISO={countryISO}
              onClick={handleOnClick}
            />
          </Suspense>
        )}
      </section>
    </Track>
  );
};
